import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Pencil, Trash2, Plus, Eye, EyeOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead, Btn } from "./kit";
import { Loading, Empty } from "@/components/site/Primitives";

const strip = ({ id, order, created_at, updated_at, ...rest }) => rest;

export const CrudPage = ({ endpoint, title, eyebrow, blank, Form, renderRow, label, testid }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/admin/${endpoint}`).then((r) => setItems(r.data)), [endpoint]);
  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      edit.id ? await api.put(`/admin/${endpoint}/${edit.id}`, strip(edit)) : await api.post(`/admin/${endpoint}`, strip(edit));
      toast.success(`${label} saved`); setEdit(null); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const togglePublish = async (item) => {
    try { await api.put(`/admin/${endpoint}/${item.id}`, strip({ ...item, published: !item.published })); toast.success(item.published ? "Unpublished" : "Published"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const move = async (i, dir) => {
    const next = [...items]; [next[i], next[i + dir]] = [next[i + dir], next[i]];
    setItems(next);
    try { await api.post(`/admin/${endpoint}/reorder`, next.map((x) => x.id)); } catch (e) { toast.error(errMsg(e)); load(); }
  };
  const remove = async (item) => {
    if (!window.confirm(`Delete this ${label.toLowerCase()}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/${endpoint}/${item.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid={testid}>
      <PageHead eyebrow={eyebrow} title={title}>
        {isAdmin && <Btn onClick={() => setEdit({ ...blank })} data-testid={`${endpoint}-create-btn`}><Plus size={16} />New {label.toLowerCase()}</Btn>}
      </PageHead>
      {!isAdmin && <p className="mb-4 text-xs text-zinc-500">View only — administrator role required to edit.</p>}
      {!items ? <Loading rows={1} /> : items.length === 0 ? <Empty title={`No ${label.toLowerCase()} entries yet.`} body="Nothing is shown publicly until you create and publish an entry." /> : (
        <ul className="border border-[#27272A] divide-y divide-[#27272A]">
          {items.map((item, i) => (
            <li key={item.id} className="flex items-center gap-4 p-4 hover:bg-[#0a0a0a]" data-testid={`${endpoint}-row-${i}`}>
              {isAdmin && <div className="flex flex-col">
                <button disabled={i === 0} onClick={() => move(i, -1)} className="p-1 text-zinc-500 hover:text-white disabled:opacity-20" aria-label="Move up" data-testid={`${endpoint}-up-${i}`}><ArrowUp size={14} /></button>
                <button disabled={i === items.length - 1} onClick={() => move(i, 1)} className="p-1 text-zinc-500 hover:text-white disabled:opacity-20" aria-label="Move down" data-testid={`${endpoint}-down-${i}`}><ArrowDown size={14} /></button>
              </div>}
              <div className="flex-1 min-w-0">{renderRow(item)}</div>
              {isAdmin && <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => togglePublish(item)} className={`p-2 ${item.published ? "text-[#00E5FF]" : "text-zinc-600"} hover:text-white`} aria-label={item.published ? "Unpublish" : "Publish"} title={item.published ? "Published — click to unpublish" : "Unpublished — click to publish"} data-testid={`${endpoint}-publish-${i}`}>{item.published ? <Eye size={16} /> : <EyeOff size={16} />}</button>
                <button onClick={() => setEdit({ ...item })} className="p-2 text-zinc-500 hover:text-white" aria-label="Edit" data-testid={`${endpoint}-edit-${i}`}><Pencil size={16} /></button>
                <button onClick={() => remove(item)} className="p-2 text-zinc-500 hover:text-red-400" aria-label="Delete" data-testid={`${endpoint}-delete-${i}`}><Trash2 size={16} /></button>
              </div>}
            </li>
          ))}
        </ul>
      )}
      {edit && (
        <Dialog open onOpenChange={(o) => !o && setEdit(null)}>
          <DialogContent className="max-w-3xl bg-[#0a0a0a] border-[#27272A] max-h-[90vh] overflow-auto rounded-none" data-testid={`${endpoint}-editor`}>
            <DialogHeader><DialogTitle className="font-display text-2xl">{edit.id ? `Edit ${label.toLowerCase()}` : `New ${label.toLowerCase()}`}</DialogTitle></DialogHeader>
            <Form value={edit} set={(k) => (v) => setEdit((x) => ({ ...x, [k]: v }))} />
            <div className="flex justify-end gap-2 pt-4 border-t border-[#27272A]">
              <Btn variant="ghost" onClick={() => setEdit(null)}>Cancel</Btn>
              <Btn onClick={save} disabled={busy} data-testid={`${endpoint}-save-btn`}>{busy ? "Saving" : "Save"}</Btn>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
